import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, AppState, AppStateStatus } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { useStyles } from '../../hooks/useStyles';
import { authApi } from '../../shared/utils/api';
import { CommentThread, CommentContextType } from '../../shared/types/comments';
import { eventEmitter, THREAD_EVENTS } from '../../shared/utils/eventEmitter';
import { PageContainer, PageHeader } from '../../components/layout';
import { ThreadList } from '../../components/comments';
import { DiscussionModal } from '../../components/discussions';
import { logger } from '../../shared/utils/logger';

const POLL_INTERVAL = 30000; // 30 seconds - reduced from 3s to prevent rate limiting

export default function DiscussionsPage() {
  const router = useRouter();
  const styles = useStyles();

  const [threads, setThreads] = useState<CommentThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<CommentContextType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const appStateRef = useRef<AppStateStatus>(AppState.currentState);
  
  // Modal state for viewing discussions without navigating
  const [selectedThread, setSelectedThread] = useState<CommentThread | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  // Load threads function
  const loadThreads = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      
      logger.debug('Loading threads with filter', 'discussions', { filter });
      const response = await authApi.getAllThreads({
        context_type: filter !== 'all' ? filter : undefined,
        limit: 100,
      });
      
      logger.debug('API response received', 'discussions', {
        threadsCount: response?.threads?.length || 0,
        hasThreads: !!response?.threads,
      });
      
      // Handle response structure
      const threadsArray = response?.threads || [];
      logger.debug('Setting threads array', 'discussions', { count: threadsArray.length });
      setThreads(threadsArray);
      
      if (threadsArray.length === 0) {
        logger.warn('No threads returned from API', 'discussions');
      }
    } catch (error: any) {
      logger.error('Error loading threads', error, 'discussions', {
        filter,
        message: error?.message,
        status: error?.status,
        data: error?.data,
        stack: error?.stack,
      });
      // Set empty array on error to prevent stale data
      setThreads([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // Start polling
  const startPolling = () => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // Start new polling interval
    pollingIntervalRef.current = setInterval(() => {
      // Only poll if app is in foreground
      if (appStateRef.current === 'active') {
        loadThreads(true);
      }
    }, POLL_INTERVAL);
  };

  // Stop polling
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Handle app state changes (foreground/background)
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (appStateRef.current.match(/inactive|background/) && nextAppState === 'active') {
        // App has come to foreground - reload immediately and restart polling
        logger.debug('App came to foreground, reloading threads', 'discussions');
        loadThreads();
        startPolling();
      } else if (nextAppState.match(/inactive|background/)) {
        // App has gone to background - stop polling to save resources
        logger.debug('App went to background, stopping polling', 'discussions');
        stopPolling();
      }
      appStateRef.current = nextAppState;
    });

    return () => {
      subscription.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load threads on mount and when filter changes
  useEffect(() => {
    loadThreads();
    startPolling();
    
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Listen for thread creation and update events to refresh immediately
  useEffect(() => {
    logger.debug('Setting up event listeners', 'discussions');
    const unsubscribeCreated = eventEmitter.on(THREAD_EVENTS.CREATED, (thread) => {
      logger.debug('Thread created event received', 'discussions', { threadId: thread?.id });
      // Refresh threads immediately when a new thread is created
      loadThreads(true); // Silent refresh to avoid loading spinner
    });

    const unsubscribeCommentAdded = eventEmitter.on(THREAD_EVENTS.COMMENT_ADDED, (thread) => {
      logger.debug('Comment added event received', 'discussions', { threadId: thread?.id });
      // Refresh threads to update comment counts and timestamps
      loadThreads(true); // Silent refresh to avoid loading spinner
    });

    return () => {
      logger.debug('Cleaning up event listeners', 'discussions');
      unsubscribeCreated();
      unsubscribeCommentAdded();
    };
  }, [loadThreads]); // Re-subscribe when loadThreads changes (which happens when filter changes)

  // Reload when page comes into focus
  useFocusEffect(
    React.useCallback(() => {
      logger.debug('Page focused, reloading threads', 'discussions', { currentCount: threads.length });
      loadThreads();
      startPolling();
      
      return () => {
        // Don't stop polling on blur - keep it running for real-time updates
        // Only stop when component unmounts
      };
    }, [filter, loadThreads])
  );


  // Open modal to view/interact with thread without navigating
  const handleThreadPress = (thread: CommentThread) => {
    setSelectedThread(thread);
    setModalVisible(true);
  };

  // Navigate to the full page view of the thread's context
  const handleOpenFullPage = () => {
    if (!selectedThread) return;
    
    // Close modal first
    setModalVisible(false);
    
    // Navigate to the appropriate page based on context type, with thread ID as query param
    const threadIdParam = selectedThread.id ? `?threadId=${selectedThread.id}` : '';
    
    switch (selectedThread.context_type) {
      case 'portfolio':
        router.push(`/(tabs)/portfolios/${selectedThread.context_id}${threadIdParam}`);
        break;
      case 'property':
        // Use portfolio_id if available, otherwise navigate to portfolios list
        if (selectedThread.portfolio_id) {
          router.push(`/(tabs)/portfolios/${selectedThread.portfolio_id}/properties/${selectedThread.context_id}${threadIdParam}`);
        } else {
          // Fallback: try to find portfolio by looking up property
          router.push('/(tabs)/portfolios');
        }
        break;
      case 'mortgage_calculation':
      case 'financial_entry':
      case 'due_diligence_item':
      case 'survey_item':
        // These are typically on property pages - would need portfolio_id lookup
        // For now, navigate to portfolios list
        router.push('/(tabs)/portfolios');
        break;
      default:
        router.push('/(tabs)/portfolios');
    }
  };

  // Close the modal
  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedThread(null);
    // Refresh threads to get updated comment counts
    loadThreads(true);
  };


  return (
    <PageContainer scrollable={false} verticalPadding="md">
      <PageHeader
        title="Discussions"
        subtitle="All ongoing conversations and discussions"
      />

      {/* Threads List */}
      <View style={{ flex: 1, minHeight: 0 }}>
        <ThreadList
          threads={threads}
          loading={loading && threads.length === 0}
          filter={filter}
          onFilterChange={setFilter}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onThreadPress={handleThreadPress}
        />
      </View>

      {/* Discussion Modal - View/interact with thread without navigating */}
      <DiscussionModal
        visible={modalVisible}
        thread={selectedThread}
        onClose={handleCloseModal}
        onOpenFullPage={handleOpenFullPage}
      />
    </PageContainer>
  );
}

